/**
 * Reusable CSV export button component.
 * Builds a CSV string from headers + rows and triggers a browser download.
 * Includes BOM for Unicode support.
 */

import { Download } from 'lucide-react';

import { Button } from '@/components/ui/Button';

// ============================================================================
// TYPES
// ============================================================================

export interface CsvExportProps {
  headers: string[];
  rows: (string | number | null)[][];
  filename: string;
  className?: string;
  label?: string; // Phase 37.1 D-C1: override default "Export CSV" text
}

// ============================================================================
// HELPERS
// ============================================================================

function escapeCsvValue(value: string | number | null): string {
  if (value === null || value === undefined) return '';
  const str = String(value);
  // Escape if value contains comma, quote, or newline
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

function buildCsvString(
  headers: string[],
  rows: (string | number | null)[][]
): string {
  const headerLine = headers.map(escapeCsvValue).join(',');
  const dataLines = rows.map((row) => row.map(escapeCsvValue).join(','));
  return [headerLine, ...dataLines].join('\n');
}

// ============================================================================
// COMPONENT
// ============================================================================

/**
 * Trigger a CSV download from headers + rows data.
 * Exported for use by tab-level combined export buttons.
 */
export function downloadCsv(
  headers: string[],
  rows: (string | number | null)[][],
  filename: string
) {
  const csvContent = buildCsvString(headers, rows);
  const bom = '\uFEFF';
  const blob = new Blob([bom + csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename.endsWith('.csv') ? filename : `${filename}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

/**
 * Builds a multi-section CSV string with section headers separating each
 * data block. Used by tab-level combined export.
 */
export function buildMultiSectionCsv(
  sections: { title: string; headers: string[]; rows: (string | number | null)[][] }[]
): string {
  const parts: string[] = [];
  for (const section of sections) {
    parts.push(`# ${section.title}`);
    parts.push(buildCsvString(section.headers, section.rows));
    parts.push(''); // blank line separator
  }
  return parts.join('\n');
}

export function downloadMultiSectionCsv(
  sections: { title: string; headers: string[]; rows: (string | number | null)[][] }[],
  filename: string
) {
  const csvContent = buildMultiSectionCsv(sections);
  const bom = '\uFEFF';
  const blob = new Blob([bom + csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename.endsWith('.csv') ? filename : `${filename}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

export function CsvExport({ headers, rows, filename, className, label }: CsvExportProps) {
  return (
    <Button
      variant="secondary"
      size="sm"
      onClick={() => downloadCsv(headers, rows, filename)}
      className={className}
      data-csv-component=""
    >
      <Download size={14} />
      {label ?? 'Export CSV'}
    </Button>
  );
}
